import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  REPORT_DATE_RANGE_OPTIONS,
  REPORT_FILTERS,
  REPORT_FORMAT_OPTIONS,
  REPORT_SCOPE_OPTIONS,
  filter_reports,
  load_company_reports,
} from './company_reports_helpers';
import { download_company_report } from './company_reports_download_helpers';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { can_route_case } from '../CompanyCases/company_cases_permissions';
import {
  read_session_current_role,
  read_session_project_id,
  to_number,
} from '../CompanyCases/company_cases_common';
import {
  fetch_current_user_case_scope,
  fetch_entity_types,
} from '../CompanyCases/company_cases_helpers';
import { load_visible_open_company_cases } from '../CompanyCases/company_cases_loading';
import { fetch_intake_cases } from '@/views/x-verdict_views/IntakeOfficer/intake_case_queue_service';
import { listXVerdictEntityConfigs } from '@/services/xverdict_entity_config_service';
import {
  CASE_REPORT_DATA_URL,
  CHECK_CASE_REPORT_URL,
  GENERATE_CASE_REPORT_URL,
  GET_CASE_REPORT_AUDIT_TRAIL_URL,
  GET_CASE_REPORT_CONFIGURATION_URL,
  GET_CASE_REPORT_HISTORY_URL,
  GET_CASE_REPORT_LOOKUPS_URL,
  GET_COMPANIES,
  LIST_ENTITY_PROFILES_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

function parse_api_data(response) {
  const raw = response?.data ?? response;
  if (typeof raw === 'string') return JSON.parse(raw);
  return raw || {};
}

function safe_array(value) {
  return Array.isArray(value) ? value : [];
}
const REPORT_OUTPUT_FORMATS = ['xlsx', 'pdf'];
const CASE_REPORT_CONFIGURATION_STORAGE_KEY = 'probaworkflows.caseReportConfiguration.v1';

function project_option(project_id, name = '') {
  return {
    id: Number(project_id),
    name: name || `Project ${project_id}`,
  };
}

function project_id_from_record(record = {}) {
  return to_number(
    record?.project_id
    || record?.project?.id
    || record?.project
    || record?.entity_config?.project_id
    || record?.entity_config?.project?.id
    || record?.config_data?.project_id
    || record?.config_data?.project?.id
  );
}

function project_name_from_record(record = {}) {
  return [
    record?.project_name,
    record?.project?.project_name,
    record?.project?.name,
    record?.entity_config?.project_name,
    record?.entity_config?.project?.project_name,
    record?.entity_config?.project?.name,
    record?.config_data?.project_name,
    record?.config_data?.project?.project_name,
    record?.config_data?.project?.name,
  ].find(Boolean) || '';
}

function entity_type_id_from_config(config = {}) {
  return to_number(config?.entity_type || config?.entity_config?.entity_type);
}

function is_coordinator_role(role = '') {
  return String(role || '').toLowerCase().includes('coordinator');
}

function is_case_owner_role(role = '') {
  const normalized_role = String(role || '').toLowerCase();
  return (
    normalized_role.includes('case owner')
    || normalized_role.includes('case_owner')
    || normalized_role === 'owner'
    || normalized_role.endsWith(' owner')
  );
}

function is_intake_officer_role(role = '') {
  const normalized_role = String(role || '').toLowerCase();
  return (
    normalized_role === 'intake'
    || normalized_role === 'intake desk'
    || normalized_role === 'intake officer'
    || normalized_role === 'intake_officer'
    || normalized_role.includes('intake desk')
    || normalized_role.includes('intake officer')
    || normalized_role.includes('intake_officer')
  );
}

function most_common_project_option(cases = []) {
  const project_counts = new Map();

  safe_array(cases).forEach((case_item) => {
    const project_id = project_id_from_record(case_item);
    if (!project_id) return;

    const current = project_counts.get(String(project_id)) || {
      id: project_id,
      name: project_name_from_record(case_item),
      count: 0,
    };
    current.count += 1;
    project_counts.set(String(project_id), current);
  });

  const project = Array.from(project_counts.values())
    .sort((left, right) => right.count - left.count)[0];

  return project ? project_option(project.id, project.name) : null;
}

function format_date_time(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function create_company_reports_state() {
  return {
    active_filter: 'all',
    filters: REPORT_FILTERS.map((filter) => ({ ...filter })),
    report_date_ranges: REPORT_DATE_RANGE_OPTIONS.map((option) => ({ ...option })),
    report_scopes: REPORT_SCOPE_OPTIONS.map((option) => ({ ...option })),
    report_formats: REPORT_FORMAT_OPTIONS.map((option) => ({ ...option })),
    report_modal_open: false,
    selected_report_id: '',
    selected_date_range: 'last-90-days',
    selected_scope: 'my-caseload',
    selected_format: 'xlsx',
    active_builder_tab: 'generate',
    selected_project_id: '',
    available_projects: [],
    project_profile_counts: {},
    coordinator_scope: null,
    loading_projects: false,
    report_form: {
      start_date: '',
      end_date: '',
      case_origin: '',
      case_status: '',
      client_type: '',
      gender: '',
      ethnicity: '',
    },
    report_fields: [
      { key: 'client_name', label: 'Client name', aliases: ['client_case_name', 'title'] },
      { key: 'city', label: 'City', aliases: ['city'] },
      { key: 'age', label: 'Age', aliases: ['age'] },
      { key: 'ethnicity', label: 'Ethnicity', aliases: ['ethnicity'] },
      { key: 'gender', label: 'Gender', aliases: ['gender'] },
      { key: 'case_journal', label: 'Case Journal', aliases: ['description', 'action'] },
      { key: 'case_number', label: 'Case number', aliases: ['case_code', 'matter_id'] },
      { key: 'case_status', label: 'Case Status', aliases: ['status'] },
      { key: 'main_category', label: 'Main Category', aliases: ['category', 'practice_area'] },
      { key: 'case_record_type', label: 'Case Record Type', aliases: ['report', 'domain'] },
      { key: 'date_opened', label: 'Date Opened', aliases: ['date'] },
      { key: 'case_origin', label: 'Case Origin', aliases: ['domain'] },
      { key: 'created_by', label: 'Created By', aliases: ['owner', 'user'] },
      { key: 'client_type', label: 'Client Type', aliases: ['scope'] },
      { key: 'case_facts', label: 'Case Facts', aliases: ['description', 'metric'] },
      { key: 'sub_category', label: 'Sub Category', aliases: ['section', 'task'] },
    ],
    selected_field_keys: [],
    excel_field_keys: [],
    field_search: '',
    dynamic_report_lookups: {
      fields: [],
      dynamic_fields: [],
      client_types: [],
      case_statuses: [],
      output_formats: [],
    },
    dynamic_filter_rows: [],
    preview_rows: [],
    preview_fields: [],
    preview_count: 0,
    generated_history: [],
    generated_audit_trail: [],
    generated_history_page: 1,
    generated_history_page_size: 7,
    generated_report_preview_open: false,
    generated_report_preview_title: '',
    generated_report_preview_type: 'excel',
    generated_report_preview_url: '',
    generated_report_preview_rows: [],
    generated_report_preview_fields: [],
    generated_report_preview_error: '',
    loading_generated_report_preview: false,
    loading_reports: false,
    loading_dynamic_report: false,
    previewing_dynamic_report: false,
    checking_dynamic_report: false,
    generating_dynamic_report: false,
    loading_dynamic_history: false,
    reports: [],
    reports_error: '',
  };
}

export default {
  name: 'CompanyReports',
  mixins: [alert_mixin],
  components: {
    BlockingLoaderOverlay,
  },
  data() {
    return create_company_reports_state();
  },
  computed: {
    project_id() {
      return to_number(
        this.selected_project_id
        || this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    route_or_session_project_id() {
      return to_number(
        this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id()
      );
    },
    current_role() {
      return this.$store?.getters?.currentRole || read_session_current_role();
    },
    can_route_cases() {
      return can_route_case(this);
    },
    filtered_reports() {
      return filter_reports(this.reports, this.active_filter);
    },
    selected_report() {
      return this.reports.find((report) => report.id === this.selected_report_id)
        || this.filtered_reports[0]
        || null;
    },
    audit_report() {
      return this.reports.find((report) => report.id === 'compliance-audit-activity')
        || this.reports.find((report) => report.category_key === 'compliance')
        || null;
    },
    current_user_name() {
      return this.$store?.getters?.currentLeadName || 'Current User';
    },
    case_origin_options() {
      return this.unique_report_values(['domain', 'province', 'case_origin']);
    },
    case_status_options() {
      return safe_array(this.dynamic_report_lookups.case_statuses);
    },
    client_type_options() {
      if (this.is_coordinator_scope_report) {
        const scope_id = String(this.coordinator_scope_entity_type_id);
        const scoped_option = safe_array(this.dynamic_report_lookups.client_types)
          .find((option) => String(option?.id) === scope_id);

        return [
          scoped_option || {
            id: this.coordinator_scope_entity_type_id,
            name: this.coordinator_scope_domain_name,
          },
        ];
      }

      return safe_array(this.dynamic_report_lookups.client_types);
    },
    is_coordinator_scope_report() {
      return is_coordinator_role(this.current_role) && Boolean(this.coordinator_scope_entity_type_id);
    },
    coordinator_scope_entity_type_id() {
      return to_number(this.coordinator_scope?.entity_type_id);
    },
    coordinator_scope_domain_name() {
      return this.coordinator_scope?.entity_type_name || `Domain ${this.coordinator_scope_entity_type_id}`;
    },
    static_report_fields() {
      const fields = safe_array(this.dynamic_report_lookups.fields);
      return fields.map((field) => ({
        key: field.key,
        label: field.label || field.key,
        meta: 'Case field',
        dynamic: false,
      })).filter((field) => field.key);
    },
    dynamic_report_fields() {
      return safe_array(this.dynamic_report_lookups.dynamic_fields)
        .map((field) => ({
          key: field.key,
          section_question_id: field.section_question_id,
          question_id: field.question_id,
          label: field.label || field.key,
          meta: [field.form, field.section, field.question_type].filter(Boolean).join(' | '),
          dynamic: true,
        }))
        .filter((field) => field.key);
    },
    all_report_fields() {
      return [...this.static_report_fields, ...this.dynamic_report_fields];
    },
    filtered_static_report_fields() {
      return this.filter_report_fields(this.static_report_fields);
    },
    filtered_dynamic_report_fields() {
      return this.filter_report_fields(this.dynamic_report_fields);
    },
    selected_fields() {
      const selected = new Set(this.selected_field_keys.map(String));
      return this.all_report_fields.filter((field) => selected.has(String(field.key)));
    },
    selected_dynamic_fields() {
      return this.selected_fields.filter((field) => field.dynamic);
    },
    selected_static_field_count() {
      return this.selected_fields.filter((field) => !field.dynamic).length;
    },
    selected_dynamic_field_count() {
      return this.selected_dynamic_fields.length;
    },
    selected_excel_field_count() {
      return this.selected_download_field_keys().length;
    },
    report_columns() {
      const fields = this.preview_fields.length ? this.preview_fields : this.selected_download_field_keys();
      return fields.map((key) => ({ key, label: this.report_field_label(key) }));
    },
    generated_report_preview_columns() {
      return this.generated_report_preview_fields.map((key) => ({
        key,
        label: this.report_field_label(key),
      }));
    },
    visible_preview_rows() {
      return this.preview_rows.slice(0, 75);
    },
    output_format_options() {
      const formats = safe_array(this.dynamic_report_lookups.output_formats);
      const available = formats.length ? formats : REPORT_OUTPUT_FORMATS;
      return available.filter((format) => REPORT_OUTPUT_FORMATS.includes(format));
    },
    output_format_label() {
      return (format) => (format === 'xlsx' ? 'Excel' : String(format).toUpperCase());
    },
    active_history_rows() {
      return this.generated_history;
    },
    active_history_count() {
      return this.active_history_rows.length;
    },
    history_page_count() {
      return Math.max(1, Math.ceil(this.active_history_count / this.generated_history_page_size));
    },
    bounded_history_page() {
      const page = Number(this.generated_history_page) || 1;
      return Math.min(Math.max(page, 1), this.history_page_count);
    },
    paginated_history_rows() {
      const start = (this.bounded_history_page - 1) * this.generated_history_page_size;
      return this.active_history_rows.slice(start, start + this.generated_history_page_size);
    },
    history_page_start() {
      if (!this.active_history_count) return 0;
      return ((this.bounded_history_page - 1) * this.generated_history_page_size) + 1;
    },
    history_page_end() {
      return Math.min(
        this.bounded_history_page * this.generated_history_page_size,
        this.active_history_count
      );
    },
    history_page_numbers() {
      const total = this.history_page_count;
      const current = this.bounded_history_page;
      const visible_count = Math.min(total, 5);
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + visible_count - 1);
      start = Math.max(1, end - visible_count + 1);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },
    total_loading() {
      return this.loading_projects || this.loading_reports || this.loading_dynamic_report;
    },
    page_loading_message() {
      return 'Loading reports...';
    },
    page_loading_detail() {
      return 'Fetching the available company report templates.';
    },
  },
  mounted() {
    this.bootstrap_reports();
  },
  watch: {
    project_id(new_value, old_value) {
      if (!new_value || String(new_value) === String(old_value)) return;
      this.preview_rows = [];
      this.preview_fields = [];
      this.preview_count = 0;
      this.generated_history = [];
      this.generated_audit_trail = [];
      this.reset_generated_history_page();
      this.load_reports();
      this.load_dynamic_report_screen();
    },
  },
  methods: {
    async bootstrap_reports() {
      await this.fetch_projects();
      await Promise.all([
        this.load_reports(),
        this.load_dynamic_report_screen(),
      ]);
    },
    async fetch_projects() {
      this.loading_projects = true;
      try {
        const response = await axios.get(GET_COMPANIES, get_request_config());
        const parsed = parse_api_data(response);
        const items = safe_array(parsed.data || parsed.projects || parsed.results);
        this.available_projects = items
          .map((project) => ({
            id: project.id,
            name: project.name || project.project_name || project.title || `Project ${project.id}`,
          }))
          .filter((project) => project.id);

        await this.resolve_report_project_context();
        await this.load_project_profile_counts();
        await this.resolve_report_project_with_data();
      } catch (error) {
        this.available_projects = [];
        this.project_profile_counts = {};
        await this.resolve_report_project_context();
        this.reports_error = this.project_id
          ? ''
          : this.report_error_message(error, 'Failed to load your project context.');
      } finally {
        this.loading_projects = false;
      }
    },
    async resolve_report_project_context() {
      const scoped_project = await this.resolve_scoped_report_project();
      const preferred_project_id = this.route_or_session_project_id || scoped_project?.id;

      if (preferred_project_id) {
        const matching_project = this.available_projects.find(
          (project) => String(project.id) === String(preferred_project_id)
        );
        const matches_scoped_project = String(scoped_project?.id || '') === String(preferred_project_id);

        if (matching_project || matches_scoped_project || !this.available_projects.length) {
          if (!matching_project) {
            this.available_projects = [
              ...this.available_projects,
              project_option(
                preferred_project_id,
                scoped_project?.name || this.$store?.getters?.currentProjectName
              ),
            ];
          }
          this.selected_project_id = String(preferred_project_id);
          return;
        }
      }

      if (!this.selected_project_id && this.available_projects.length) {
        this.selected_project_id = String(this.available_projects[0].id);
      }
    },
    async resolve_scoped_report_project() {
      return await this.resolve_coordinator_report_project()
        || await this.resolve_case_owner_report_project()
        || await this.resolve_intake_report_project();
    },
    async resolve_coordinator_report_project() {
      if (!is_coordinator_role(this.current_role)) {
        this.coordinator_scope = null;
        return null;
      }

      const scope = await fetch_current_user_case_scope().catch(() => null);
      this.coordinator_scope = scope;
      if (!scope) return null;

      const scope_project_id = to_number(scope.project_id);
      if (scope_project_id) {
        return project_option(scope_project_id, scope.project_name);
      }

      const entity_type_id = to_number(scope.entity_type_id);
      if (!entity_type_id) return null;

      const entity_type_project = await this.resolve_project_from_scoped_entity_type(entity_type_id);
      if (entity_type_project) return entity_type_project;

      return this.resolve_project_from_scoped_entity_config(entity_type_id);
    },
    async resolve_case_owner_report_project() {
      if (!is_case_owner_role(this.current_role)) {
        return null;
      }

      const cases = await load_visible_open_company_cases({
        current_role: this.current_role,
        can_route_cases: false,
      }).catch(() => []);

      return most_common_project_option(cases);
    },
    async resolve_intake_report_project() {
      if (!is_intake_officer_role(this.current_role)) {
        return null;
      }

      const cases = await fetch_intake_cases().catch(() => []);
      return most_common_project_option(cases);
    },
    async resolve_project_from_scoped_entity_type(entity_type_id) {
      const entity_types = await fetch_entity_types().catch(() => []);
      const entity_type = safe_array(entity_types).find(
        (item) => Number(item?.id || item?.entity_type_id) === Number(entity_type_id)
      );
      const project_id = project_id_from_record(entity_type);

      return project_id ? project_option(project_id, project_name_from_record(entity_type)) : null;
    },
    async resolve_project_from_scoped_entity_config(entity_type_id) {
      const configs = await listXVerdictEntityConfigs().catch(() => []);
      const config = safe_array(configs).find(
        (item) => Number(entity_type_id_from_config(item)) === Number(entity_type_id)
      );
      const project_id = project_id_from_record(config);

      return project_id ? project_option(project_id, project_name_from_record(config)) : null;
    },
    async load_project_profile_counts() {
      const counts = await Promise.all(
        this.available_projects.map(async (project) => [
          String(project.id),
          await this.fetch_project_profile_count(project.id),
        ])
      );
      this.project_profile_counts = Object.fromEntries(counts);
    },
    async resolve_report_project_with_data() {
      if (!this.available_projects.length || !this.selected_project_id) return;

      const selected_count = this.project_reportable_count(this.selected_project_id);
      if (selected_count > 0) return;

      const project_with_data = this.available_projects.find(
        (project) => this.project_reportable_count(project.id) > 0
      );
      if (project_with_data) {
        this.selected_project_id = String(project_with_data.id);
      }
    },
    async fetch_project_profile_count(project_id) {
      const response = await axios.get(
        LIST_ENTITY_PROFILES_URL,
        get_request_config({
          params: {
            project_id,
            page: 1,
            page_size: 1,
          },
        })
      ).catch(() => null);

      if (!response) return 0;

      const parsed = parse_api_data(response);
      return Number(parsed.pagination?.total_count || safe_array(parsed.data).length || 0);
    },
    project_reportable_count(project_id) {
      const count = this.project_profile_counts[String(project_id)];
      return Number.isFinite(Number(count)) ? Number(count) : null;
    },
    change_builder_tab(tab) {
      this.active_builder_tab = tab;
    },
    set_filter(filter_key) {
      this.active_filter = filter_key;
      this.selected_report_id = '';
    },
    open_report_modal(report) {
      this.selected_report_id = report.id;
      this.selected_date_range = 'last-90-days';
      this.selected_scope = 'my-caseload';
      this.selected_format = 'xlsx';
      this.report_modal_open = true;
    },
    close_report_modal() {
      this.report_modal_open = false;
    },
    generate_report() {
      this.generate_dynamic_report();
      this.close_report_modal();
    },
    download_report(format = this.selected_format) {
      this.selected_format = format;
      this.generate_dynamic_report();
    },
    download_static_report(format = this.selected_format) {
      if (!this.selected_report) return;

      this.selected_format = format;
      download_company_report(this.prepared_report(this.selected_report), {
        date_range: this.report_date_range(),
        scope: this.selected_scope,
        format,
      });
    },
    download_audit_report() {
      if (!this.audit_report) return;

      download_company_report(this.prepared_report(this.audit_report), {
        date_range: this.report_date_range(),
        scope: this.selected_scope,
        format: 'pdf',
      });
    },
    async load_reports() {
      this.loading_reports = true;
      this.reports_error = '';

      try {
        this.reports = await load_company_reports({
          project_id: this.project_id,
          current_role: this.current_role,
          can_route_cases: this.can_route_cases,
        });
      } catch (error) {
        this.reports_error = this.report_error_message(error);
      } finally {
        this.loading_reports = false;
      }
    },
    async load_dynamic_report_screen() {
      await Promise.all([
        this.fetch_dynamic_report_lookups(),
        this.fetch_dynamic_report_history(),
      ]);
    },
    async fetch_dynamic_report_lookups() {
      if (!this.project_id) {
        this.reports_error = 'Select a project before generating reports.';
        return;
      }

      this.loading_dynamic_report = true;
      try {
        const response = await axios.get(
          GET_CASE_REPORT_LOOKUPS_URL,
          get_request_config({ params: { project_id: this.project_id } })
        );
        const parsed = parse_api_data(response);
        this.dynamic_report_lookups = {
          ...this.dynamic_report_lookups,
          ...(parsed.data || {}),
        };
        if (!(await this.restore_saved_report_configuration())) {
          this.apply_default_dynamic_fields();
        }
        this.apply_coordinator_scope_filter();
        if (!this.output_format_options.includes(this.selected_format)) {
          this.selected_format = this.output_format_options[0] || 'xlsx';
        }
      } catch (error) {
        this.reports_error = this.report_error_message(error, 'Failed to load dynamic report fields.');
      } finally {
        this.loading_dynamic_report = false;
      }
    },
    apply_default_dynamic_fields() {
      const available_keys = new Set(this.all_report_fields.map((field) => String(field.key)));
      const selected = this.selected_field_keys.filter((key) => available_keys.has(String(key)));
      if (selected.length) {
        this.selected_field_keys = selected;
        this.sync_excel_field_keys_with_selected_fields();
        return;
      }

      const backend_question_keys = this.dynamic_report_fields.slice(0, 12).map((field) => field.key);
      if (backend_question_keys.length) {
        this.selected_field_keys = backend_question_keys;
        this.sync_excel_field_keys_with_selected_fields();
        return;
      }

      const preferred = ['client_name', 'case_number', 'date_opened', 'status_name'];
      this.selected_field_keys = preferred.filter((key) => available_keys.has(key));
      this.sync_excel_field_keys_with_selected_fields();
    },
    apply_coordinator_scope_filter() {
      if (!this.is_coordinator_scope_report) return;

      const scope_id = String(this.coordinator_scope_entity_type_id);
      const available_scope = this.client_type_options.some(
        (option) => String(option?.id) === scope_id
      );
      if (available_scope) {
        this.report_form.client_type = scope_id;
      }
    },
    report_configuration_storage_key(project_id = this.project_id) {
      return `${CASE_REPORT_CONFIGURATION_STORAGE_KEY}.${project_id || 'default'}`;
    },
    normalize_saved_dynamic_filter_rows(rows = []) {
      const dynamic_keys = new Set(this.dynamic_report_fields.map((field) => String(field.key)));
      return safe_array(rows)
        .map((row) => ({
          id: row.id || Date.now() + Math.random(),
          field_key: row.field_key,
          operator: row.operator || 'contains',
          value: row.value || '',
        }))
        .filter((row) => dynamic_keys.has(String(row.field_key)));
    },
    apply_saved_report_configuration(saved = {}) {
      const available_keys = new Set(this.all_report_fields.map((field) => String(field.key)));
      const selected_keys = safe_array(saved.selected_field_keys)
        .filter((key) => available_keys.has(String(key)));
      this.selected_field_keys = selected_keys;
      this.dynamic_filter_rows = this.normalize_saved_dynamic_filter_rows(saved.dynamic_filters);
      this.sync_excel_field_keys_with_selected_fields();
      return Boolean(selected_keys.length || this.dynamic_filter_rows.length);
    },
    async restore_saved_report_configuration() {
      if (!this.project_id) return false;
      const response = await axios.get(
        GET_CASE_REPORT_CONFIGURATION_URL,
        get_request_config({ params: { project_id: this.project_id } })
      ).catch(() => null);

      if (response) {
        const parsed = parse_api_data(response);
        const saved = parsed.configuration || parsed.data || {};
        if (saved && this.apply_saved_report_configuration(saved)) {
          return true;
        }
      }

      const raw = window.localStorage.getItem(this.report_configuration_storage_key())
        || window.localStorage.getItem(this.report_configuration_storage_key(false));
      if (!raw) return false;

      const saved = JSON.parse(raw);
      return this.apply_saved_report_configuration(saved);
    },
    filter_report_fields(fields = []) {
      const search = String(this.field_search || '').trim().toLowerCase();
      if (!search) return fields;
      return fields.filter((field) =>
        [field.label, field.meta, field.key].join(' ').toLowerCase().includes(search)
      );
    },
    is_report_field_selected(key) {
      return this.selected_field_keys.map(String).includes(String(key));
    },
    toggle_report_field(key) {
      if (this.is_report_field_selected(key)) {
        this.selected_field_keys = this.selected_field_keys.filter((field_key) => String(field_key) !== String(key));
        this.sync_excel_field_keys_with_selected_fields(false);
        return;
      }
      this.selected_field_keys = [...this.selected_field_keys, key];
      this.sync_excel_field_keys_with_selected_fields();
    },
    select_report_fields(fields = []) {
      const selected = new Set(this.selected_field_keys.map(String));
      fields.forEach((field) => {
        selected.add(String(field.key));
      });
      this.selected_field_keys = Array.from(selected);
      this.sync_excel_field_keys_with_selected_fields();
    },
    clear_report_fields() {
      this.selected_field_keys = [];
      this.excel_field_keys = [];
      this.preview_rows = [];
      this.preview_fields = [];
      this.preview_count = 0;
    },
    dynamic_selected_field_keys() {
      const dynamic_keys = new Set(this.dynamic_report_fields.map((field) => String(field.key)));
      return this.selected_field_keys.filter((key) => dynamic_keys.has(String(key)));
    },
    sync_excel_field_keys_with_selected_fields(add_missing = true) {
      const selected_keys = this.dynamic_selected_field_keys();
      const selected = new Set(selected_keys.map(String));
      const next = this.excel_field_keys.filter((key) => selected.has(String(key)));
      const next_set = new Set(next.map(String));
      if (add_missing) {
        selected_keys.forEach((key) => {
          if (!next_set.has(String(key))) {
            next.push(key);
          }
        });
      }
      this.excel_field_keys = next;
    },
    is_excel_field_selected(key) {
      return this.excel_field_keys.map(String).includes(String(key));
    },
    toggle_excel_field(key) {
      if (this.is_excel_field_selected(key)) {
        this.excel_field_keys = this.excel_field_keys.filter((field_key) => String(field_key) !== String(key));
        return;
      }
      this.excel_field_keys = [...this.excel_field_keys, key];
    },
    report_field_label(key) {
      const field = this.all_report_fields.find((item) => String(item.key) === String(key));
      return field?.label || key;
    },
    add_dynamic_filter_row() {
      const field = this.dynamic_report_fields[0];
      if (!field) {
        this.show_info('No dynamic fields are available for this project.');
        return;
      }
      this.dynamic_filter_rows.push({
        id: Date.now() + Math.random(),
        field_key: field.key,
        operator: 'contains',
        value: '',
      });
    },
    remove_dynamic_filter_row(row_id) {
      this.dynamic_filter_rows = this.dynamic_filter_rows.filter((row) => row.id !== row_id);
    },
    reset_generated_history_page() {
      this.generated_history_page = 1;
    },
    set_generated_history_page(page) {
      const numeric_page = Number(page) || 1;
      this.generated_history_page = Math.min(Math.max(numeric_page, 1), this.history_page_count);
    },
    dynamic_filter_field(row = {}) {
      return this.dynamic_report_fields.find((field) => String(field.key) === String(row.field_key)) || null;
    },
    dynamic_filter_payload() {
      return this.dynamic_filter_rows
        .map((row) => {
          const field = this.dynamic_filter_field(row);
          const values = String(row.value || '')
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);

          if (!field || !values.length) return null;
          const payload = {
            operator: row.operator,
            values,
          };
          if (field.section_question_id) {
            payload.section_question_id = Number(field.section_question_id);
          } else if (field.question_id) {
            payload.question_id = Number(field.question_id);
          }
          return payload;
        })
        .filter(Boolean);
    },
    selected_download_field_keys() {
      const selected_keys = new Set(this.dynamic_selected_field_keys().map(String));
      return this.excel_field_keys.filter((key) => selected_keys.has(String(key)));
    },
    compact_values(values) {
      const items = Array.isArray(values) ? values : [values];
      return items.map(String).filter(Boolean);
    },
    dynamic_report_payload() {
      const fields = this.selected_download_field_keys();
      const domain_values = this.report_domain_values();
      return {
        project_id: Number(this.project_id),
        applicationtype: domain_values,
        case_record_type: domain_values,
        case_status: this.compact_values(this.report_form.case_status),
        start_date: this.report_form.start_date || null,
        end_date: this.report_form.end_date || null,
        fields,
        report_fields: fields,
        dynamic_filters: this.dynamic_filter_payload(),
        output_format: this.selected_format,
      };
    },
    report_domain_values() {
      const selected_domains = this.compact_values(this.report_form.client_type);
      if (selected_domains.length) {
        return selected_domains;
      }

      if (this.is_coordinator_scope_report) {
        return [String(this.coordinator_scope_entity_type_id)];
      }

      return [];
    },
    validate_dynamic_report() {
      if (!this.project_id) {
        this.show_error('Select a project before generating reports.');
        return false;
      }
      if (!this.selected_download_field_keys().length) {
        this.show_error('Select at least one field to include in the report.');
        return false;
      }
      if (this.project_reportable_count(this.project_id) === 0) {
        this.show_error('Select a project with case data before generating reports.');
        return false;
      }
      return true;
    },
    async check_dynamic_report() {
      if (!this.validate_dynamic_report()) return;

      this.checking_dynamic_report = true;
      try {
        const response = await axios.post(
          CHECK_CASE_REPORT_URL,
          this.dynamic_report_payload(),
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.show_success(`${parsed.count || 0} matching case(s) found.`);
      } catch (error) {
        this.show_error(this.report_error_message(error, 'No report data found for the selected filters.'));
      } finally {
        this.checking_dynamic_report = false;
      }
    },
    async preview_dynamic_report() {
      if (!this.validate_dynamic_report()) return;

      this.previewing_dynamic_report = true;
      try {
        const response = await axios.post(
          CASE_REPORT_DATA_URL,
          this.dynamic_report_payload(),
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.preview_rows = safe_array(parsed.data);
        this.preview_fields = safe_array(parsed.fields);
        this.preview_count = Number(parsed.count || this.preview_rows.length || 0);
        this.show_success(`${this.preview_count} row(s) loaded.`);
      } catch (error) {
        this.preview_rows = [];
        this.preview_fields = [];
        this.preview_count = 0;
        this.show_error(this.report_error_message(error, 'No report data found for the selected filters.'));
      } finally {
        this.previewing_dynamic_report = false;
      }
    },
    async generate_dynamic_report() {
      if (!this.validate_dynamic_report()) return;

      this.generating_dynamic_report = true;
      const loader = await this.show_loader('Generating dynamic case report...');
      try {
        const response = await axios.post(
          GENERATE_CASE_REPORT_URL,
          this.dynamic_report_payload(),
          get_request_config()
        );
        const parsed = parse_api_data(response);
        const report = parsed.report || parsed.data || {};
        this.show_success('Dynamic case report generated.');
        this.download_generated_report(report);
        await this.fetch_dynamic_report_history();
      } catch (error) {
        this.show_error(this.report_error_message(error, 'Failed to generate dynamic case report.'));
      } finally {
        await this.close_loader(loader);
        this.generating_dynamic_report = false;
      }
    },
    async fetch_dynamic_report_history() {
      if (!this.project_id) return;

      this.loading_dynamic_history = true;
      const config = get_request_config({ params: { project_id: this.project_id } });
      try {
        const [history_response, audit_response] = await Promise.all([
          axios.get(GET_CASE_REPORT_HISTORY_URL, config),
          axios.get(GET_CASE_REPORT_AUDIT_TRAIL_URL, config),
        ]);
        this.generated_history = safe_array(parse_api_data(history_response).data);
        this.generated_audit_trail = safe_array(parse_api_data(audit_response).data);
        this.set_generated_history_page(this.generated_history_page);
      } catch (error) {
        this.generated_history = [];
        this.generated_audit_trail = [];
        this.reset_generated_history_page();
        this.show_error(this.report_error_message(error, 'Failed to load generated report history.'));
      } finally {
        this.loading_dynamic_history = false;
      }
    },
    download_generated_report(report = {}) {
      const url = report.download_url || report.presigned_url || report.file_url;
      if (!url) {
        this.show_info('The report was generated but no file URL was returned.');
        return;
      }

      const frame = document.createElement('iframe');
      frame.src = url;
      frame.title = this.generated_report_title(report);
      frame.style.display = 'none';
      document.body.appendChild(frame);
      window.setTimeout(() => frame.remove(), 60000);
    },
    async open_saved_generated_report(report = {}) {
      const url = report.presigned_url || report.file_url;
      if (!url) {
        this.show_info('The report was generated but no file URL was returned.');
        return;
      }

      if (this.is_excel_report(report, url)) {
        await this.open_generated_excel_preview(report);
        return;
      }

      if (this.is_pdf_report(report, url)) {
        this.open_generated_pdf_preview(report, url);
        return;
      }

      this.open_generated_report_url(url);
    },
    open_generated_report_url(url) {
      const opened = window.open(url, '_blank');
      if (opened) {
        opened.opener = null;
        opened.focus();
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    is_excel_report(report = {}, url = '') {
      const extension = this.generated_report_extension(report, url);
      return ['xls', 'xlsx'].includes(extension);
    },
    is_pdf_report(report = {}, url = '') {
      return this.generated_report_extension(report, url) === 'pdf';
    },
    generated_report_extension(report = {}, url = '') {
      const output_format = report.output_format?.internal_name
        || report.output_format
        || report.details?.output_format
        || report.details?.filters?.output_format;
      if (typeof output_format === 'string' && output_format) return output_format.replace(/^\./, '').toLowerCase();
      try {
        const pathname = new URL(url).pathname;
        const extension = pathname.split('.').pop();
        return extension && extension !== pathname ? extension.toLowerCase() : '';
      } catch {
        return '';
      }
    },
    generated_report_format_label(report = {}) {
      const extension = this.generated_report_extension(
        report,
        report.presigned_url || report.file_url || report.download_url || ''
      );
      if (['xls', 'xlsx'].includes(extension)) return 'Excel';
      if (extension === 'pdf') return 'PDF';
      return extension ? extension.toUpperCase() : 'File';
    },
    generated_report_format_class(report = {}) {
      const label = this.generated_report_format_label(report).toLowerCase();
      return {
        'is-excel': label === 'excel',
        'is-pdf': label === 'pdf',
      };
    },
    async open_generated_excel_preview(report = {}) {
      this.generated_report_preview_open = true;
      this.generated_report_preview_title = this.generated_report_title(report);
      this.generated_report_preview_type = 'excel';
      this.generated_report_preview_url = '';
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = true;

      try {
        const response = await axios.post(
          CASE_REPORT_DATA_URL,
          this.generated_report_preview_payload(report),
          get_request_config()
        );
        const parsed = parse_api_data(response);
        this.generated_report_preview_rows = safe_array(parsed.data);
        this.generated_report_preview_fields = safe_array(parsed.fields);
      } catch (error) {
        this.generated_report_preview_error = this.report_error_message(
          error,
          'Failed to open the generated Excel report.'
        );
      } finally {
        this.loading_generated_report_preview = false;
      }
    },
    open_generated_pdf_preview(report = {}, url = '') {
      this.generated_report_preview_open = true;
      this.generated_report_preview_title = this.generated_report_title(report);
      this.generated_report_preview_type = 'pdf';
      this.generated_report_preview_url = url;
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = false;
    },
    close_generated_report_preview() {
      this.generated_report_preview_open = false;
      this.generated_report_preview_title = '';
      this.generated_report_preview_type = 'excel';
      this.generated_report_preview_url = '';
      this.generated_report_preview_rows = [];
      this.generated_report_preview_fields = [];
      this.generated_report_preview_error = '';
      this.loading_generated_report_preview = false;
    },
    generated_report_preview_payload(report = {}) {
      const filters = report.details?.filters || {};
      const fields = safe_array(report.details?.field_keys || filters.fields || filters.report_fields);
      const fallback_payload = this.dynamic_report_payload();
      return {
        ...filters,
        project_id: filters.project_id || this.project_id,
        fields: fields.length ? fields : fallback_payload.fields,
        report_fields: fields.length ? fields : fallback_payload.report_fields,
        output_format: filters.output_format || 'xlsx',
      };
    },
    generated_report_title(report = {}) {
      return report.name || `Case Report ${report.report_id || report.id || ''}`.trim();
    },
    generated_report_meta(report = {}) {
      return [
        report.downloaded_by || report.generated_by || 'Generated report',
        format_date_time(report.date || report.date_completed || report.date_created),
        `${report.details?.row_count ?? '-'} row(s)`,
      ].filter(Boolean).join(' | ');
    },
    row_value(row = {}, key = '') {
      const value = row[key];
      if (value === undefined || value === null || value === '') return '-';
      return String(value);
    },
    prepared_report(report = {}) {
      const rows = this.filtered_report_rows(this.filtered_by_form(report.rows || []));
      return { ...report, rows };
    },
    filtered_by_form(rows = []) {
      return rows.filter((row) => {
        return this.matches_filter(row, 'domain', this.report_form.case_origin)
          && this.matches_filter(row, 'status', this.report_form.case_status)
          && this.matches_filter(row, 'gender', this.report_form.gender)
          && this.matches_filter(row, 'ethnicity', this.report_form.ethnicity);
      });
    },
    matches_filter(row = {}, key = '', value = '') {
      if (Array.isArray(value)) {
        if (!value.length) return true;
        return value.map(String).includes(String(row?.[key] || ''));
      }
      if (!value) return true;
      return String(row?.[key] || '').toLowerCase() === String(value).toLowerCase();
    },
    filtered_report_rows(rows = []) {
      if (!this.selected_field_keys.length) return rows;

      const aliases = this.selected_field_aliases();
      const filtered_rows = rows.map((row) => this.pick_report_fields(row, aliases));
      return filtered_rows.some((row) => Object.keys(row).length) ? filtered_rows : rows;
    },
    selected_field_aliases() {
      return this.report_fields
        .filter((field) => this.selected_field_keys.includes(field.key))
        .flatMap((field) => field.aliases);
    },
    pick_report_fields(row = {}, aliases = []) {
      return aliases.reduce((fields, key) => {
        if (Object.getOwnPropertyDescriptor(row, key) !== undefined) {
          fields[key] = row[key];
        }
        return fields;
      }, {});
    },
    report_date_range() {
      const { start_date, end_date } = this.report_form;
      if (start_date && end_date) return `${start_date} to ${end_date}`;
      if (start_date) return `From ${start_date}`;
      if (end_date) return `Until ${end_date}`;
      return this.selected_date_range;
    },
    unique_report_values(keys = []) {
      const values = this.reports
        .flatMap((report) => report.rows || [])
        .flatMap((row) => keys.map((key) => row?.[key]))
        .filter((value) => value !== undefined && value !== null && value !== '');

      return [...new Set(values.map((value) => String(value)))].sort();
    },
    report_error_message(error, fallback = 'Failed to load company reports.') {
      const data = error?.response?.data;
      if (!data) return error?.message || fallback;
      if (typeof data === 'string') {
        return data.trim().startsWith('<!DOCTYPE html') || data.trim().startsWith('<html')
          ? fallback
          : data;
      }
      if (data.message) return data.message;
      if (data.detail) return data.detail;
      const first_key = Object.keys(data)[0];
      const first_value = first_key ? data[first_key] : '';
      if (Array.isArray(first_value)) return first_value.join(', ');
      if (typeof first_value === 'string') return first_value;
      return error?.message || fallback;
    },
  },
};
